import { CommandBus } from "../commands/CommandBus";
import { CommandContext } from "../commands/types";
import { normalizeShortcut, toNormalizedCombo } from "./ShortcutParser";

export interface ShortcutBinding {
  shortcut: string;
  commandId: string;
  scope?: string;
  enabled?: () => boolean;
}

export class ShortcutManager {
  private bus: CommandBus;
  private bindings = new Map<string, ShortcutBinding>();
  private activeScope = "global";

  constructor(bus: CommandBus) {
    this.bus = bus;
  }

  setScope(scope: string) {
    this.activeScope = scope || "global";
  }

  register(binding: ShortcutBinding) {
    const normalized = normalizeShortcut(binding.shortcut);
    const compositeKey = `${binding.scope || "global"}::${normalized}`;
    if (this.bindings.has(compositeKey) && process.env.NODE_ENV !== "production") {
      console.warn(`Shortcut conflict skipped: ${compositeKey}`);
      return () => {};
    }

    this.bindings.set(compositeKey, { ...binding, shortcut: normalized });
    return () => this.bindings.delete(compositeKey);
  }

  async handleKeyDown(event: KeyboardEvent, context: CommandContext = {}) {
    const combo = toNormalizedCombo(event);
    const scopedKey = `${this.activeScope}::${combo}`;
    const globalKey = `global::${combo}`;
    const binding = this.bindings.get(scopedKey) || this.bindings.get(globalKey);
    if (!binding) return false;
    if (binding.enabled && !binding.enabled()) return false;

    event.preventDefault();
    await this.bus.execute(binding.commandId, undefined, { ...context, event, source: "shortcut" });
    return true;
  }
}
